import type {
  InboxWorkflowCellComponent,
  InboxWorkflowJson,
  InboxWorkflowTableStepConfig,
} from "@kontextwerk/control-web-frame/inbox-workflow/contract"
import LetterDeliveryModeCell from "./_modules/letter-delivery-mode-cell"
import LetterRecipientDisplayCell from "./_modules/letter-recipient-display-cell"
import LetterDeliveryDecisionPreconfiguration from "./_modules/letter-delivery-decision-preconfiguration"
import LetterDeliveryDecisionMetrics from "./_modules/letter-delivery-decision-metrics"
import {
  column,
  partialMessage,
  stringValue,
} from "../../../../_shared/_logic/registry-composition"
import type {
  LetterDeliveryDecisionCommitPayload,
  LetterDeliveryDecisionDraft,
  LetterDeliveryDecisionPreviewPayload,
  LetterDeliveryDecisionRow,
} from "./contract"
import { buildLetterDeliveryModeOptions } from "./_shared/_logic/delivery-mode-options"
import {
  automaticDeliveryUnavailableReason,
  buildLetterDeliveryDecisionPayload,
  validateLetterDeliveryDecision,
} from "./_shared/_logic/02-decide-appointment-letter-delivery-payload"
import {
  LETTER_DELIVERY_DECISION_PREVIEW_KIND,
  parseLetterDeliveryDecisionPreview,
} from "./_logic/preview"
import {
  buildLetterDeliveryDecisionRows,
  createLetterDeliveryDecisionDraft,
} from "./_logic/tableRows"

export type LetterDeliveryDecisionStepOptions = {
  automaticLetterDeliveryAllowed?: boolean
  automaticLetterDeliveryDisabledReason?: string | null
}

export const letterDeliveryDecisionCells: Record<string, InboxWorkflowCellComponent> = {
  letterDeliveryMode: LetterDeliveryModeCell,
  letterRecipientDisplay: LetterRecipientDisplayCell,
}

const deliveryModeOptionsForRow = (
  row: LetterDeliveryDecisionRow,
  automaticLetterDeliveryAllowed: boolean,
  automaticLetterDeliveryDisabledReason: string | null,
  readOnly: boolean,
) => {
  const reason = automaticDeliveryUnavailableReason(
    row,
    automaticLetterDeliveryAllowed,
    automaticLetterDeliveryDisabledReason,
  )
  const options = buildLetterDeliveryModeOptions({
    automaticLetterDeliveryAllowed: !reason,
    automaticLetterDeliveryDisabledReason: reason,
  })
  const durableMode = row.deliveryDecision?.delivery_mode

  return (readOnly || row.isResolved) &&
    durableMode &&
    !options.some((option) => option.value === durableMode)
    ? [
        ...options,
        { value: durableMode, label: durableMode === "automatic" ? "Automatisch" : "Händisch" },
      ]
    : options
}

export const createLetterDeliveryDecisionStep = (
  options: LetterDeliveryDecisionStepOptions = {},
): InboxWorkflowTableStepConfig<
  LetterDeliveryDecisionRow,
  LetterDeliveryDecisionDraft,
  LetterDeliveryDecisionCommitPayload,
  LetterDeliveryDecisionPreviewPayload
> => {
  const automaticLetterDeliveryAllowed = options.automaticLetterDeliveryAllowed !== false
  const automaticLetterDeliveryDisabledReason =
    options.automaticLetterDeliveryDisabledReason ?? null

  return {
    kind: "table",
    preview: {
      kind: LETTER_DELIVERY_DECISION_PREVIEW_KIND,
      parse: parseLetterDeliveryDecisionPreview,
      errorMessage: "Terminbrief-Zustellung konnte nicht geladen werden.",
    },
    columns: [
      column("delivery_mode", "Zustellung", "letterDeliveryMode", {
        value: ({ draft }) => stringValue(draft.deliveryMode),
        patch: (value): InboxWorkflowJson => ({
          deliveryMode: stringValue(value),
        }),
        validate: ({ row, draft, readOnly }) =>
          readOnly || row.isResolved
            ? null
            : validateLetterDeliveryDecision(
                row as LetterDeliveryDecisionRow,
                draft as LetterDeliveryDecisionDraft,
                automaticLetterDeliveryAllowed,
                automaticLetterDeliveryDisabledReason,
              ),
        options: ({ row, readOnly }) =>
          deliveryModeOptionsForRow(
            row as LetterDeliveryDecisionRow,
            automaticLetterDeliveryAllowed,
            automaticLetterDeliveryDisabledReason,
            readOnly,
          ),
      }),
      column("order", "Auftrag", "letterPrintOrder"),
      column("appointment", "Termin", "letterPrintSchedule", { isPrimary: true }),
      column("recipientChoice", "Empfänger", "letterRecipientDisplay"),
    ],
    buildRows: ({
      workflow,
      step,
      stepPreview,
      rowOutcomes,
      stepPreviewLoading,
      stepPreviewError,
    }) =>
      buildLetterDeliveryDecisionRows({
        workflow,
        step,
        preview: stepPreview,
        rowOutcomes,
        previewLoading: stepPreviewLoading,
        previewError: stepPreviewError,
      }),
    draft: {
      create: (row) => createLetterDeliveryDecisionDraft(row, automaticLetterDeliveryAllowed),
      validate: (row, draft) =>
        validateLetterDeliveryDecision(
          row,
          draft,
          automaticLetterDeliveryAllowed,
          automaticLetterDeliveryDisabledReason,
        ),
    },
    preconfiguration: LetterDeliveryDecisionPreconfiguration,
    preconfigurationProps: {
      automaticLetterDeliveryAllowed,
      automaticLetterDeliveryDisabledReason,
    },
    metrics: {
      component: LetterDeliveryDecisionMetrics,
      props: { automaticLetterDeliveryAllowed, automaticLetterDeliveryDisabledReason },
      show: ({ rows }) => rows.length > 0,
      placement: "after_filters",
    },
    buildPayload: (row, draft) =>
      buildLetterDeliveryDecisionPayload(
        row,
        draft,
        automaticLetterDeliveryAllowed,
        automaticLetterDeliveryDisabledReason,
      ),
    commit: {
      label: "Zustellart speichern",
      successMessage: "Zustellart wurde gespeichert.",
      partialMessage,
    },
  }
}
